/*
 * MommyHana buy box behaviour: variant pills, quantity stepper, live price.
 *
 * Paired with snippets/mh-buy-form.liquid. No dependencies, no framework, no external request.
 *
 * The contract between the two files is a single string, the form id. The <form> carries it as
 * its `id`; everything that has to react to a variant change carries `data-mh-form="<id>"`,
 * wherever it sits in the document. That is what lets the floating bar — which is a sibling of
 * the page, not of the buy box — show the live price and submit the same form.
 *
 * Nothing here is required to buy. The markup is already a working POST to /cart/add with a real
 * variant id and quantity; this only adds switching, stepping and the price update on top.
 */
(function () {
  'use strict';

  // A page holding two buy boxes would otherwise load and run this twice, binding every
  // handler twice — a single click would then step the quantity by two.
  if (window.mhProductFormLoaded) return;
  window.mhProductFormLoaded = true;

  var MAX_QTY = 999;

  function forEach(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  function scoped(formId, selector) {
    return document.querySelectorAll(selector + '[data-mh-form="' + formId + '"]');
  }

  function readVariants(formId) {
    var holder = document.querySelector(
      'script[data-mh-variants][data-mh-form="' + formId + '"]'
    );
    if (!holder) return [];
    try {
      return JSON.parse(holder.textContent) || [];
    } catch (e) {
      // A malformed table must not take the form down with it: the markup already holds a
      // valid variant id, so leaving the pills inert still leaves the page able to sell.
      return [];
    }
  }

  function selectedOptions(formId) {
    var values = [];
    forEach(scoped(formId, 'input[data-mh-option]'), function (input) {
      if (input.checked) values[Number(input.dataset.mhIndex)] = input.value;
    });
    return values;
  }

  function matchVariant(variants, chosen) {
    for (var i = 0; i < variants.length; i++) {
      var opts = variants[i].options || [];
      var hit = true;
      for (var j = 0; j < chosen.length; j++) {
        if (chosen[j] !== undefined && opts[j] !== chosen[j]) {
          hit = false;
          break;
        }
      }
      if (hit) return variants[i];
    }
    return null;
  }

  // The quantity control is an <input type=number> with the stepper (Phase 05) or a <select>
  // with the product page's compact buy row (SPEC-PHASE1-RETHEME §7.3). Both carry data-mh-qty and
  // both expose `.value`, so this reads either without caring which it got.
  function currentQty(formId) {
    var input = scoped(formId, '[data-mh-qty]')[0];
    var n = input ? parseInt(input.value, 10) : 1;
    if (!n || n < 1) n = 1;
    if (n > MAX_QTY) n = MAX_QTY;
    return n;
  }

  function updateBuyNow(formId, variantId) {
    forEach(scoped(formId, '[data-mh-buy-now]'), function (link) {
      var base = link.dataset.mhCartUrl || '/cart';
      link.href = base + '/' + variantId + ':' + currentQty(formId);
    });
  }

  function apply(formId, variant) {
    if (!variant) return;

    forEach(scoped(formId, 'input[data-mh-variant-id]'), function (input) {
      input.value = variant.id;
    });

    forEach(scoped(formId, '[data-mh-price]'), function (el) {
      el.textContent = variant.price;
    });

    // Empty string rather than removal: `.mh-buy__was:empty` hides it, and keeping the element
    // means switching back to a variant that is on sale has somewhere to write.
    forEach(scoped(formId, '[data-mh-compare]'), function (el) {
      el.textContent = variant.compare || '';
    });

    forEach(scoped(formId, '[data-mh-atc]'), function (button) {
      button.disabled = !variant.available;

      // The sticky bar's label carries the price inside it — "ADD TO CART — RM79.00"
      // (SPEC-PHASE1-RETHEME §7.9). It cannot be a nested [data-mh-price] span, because this
      // loop rewrites the button's whole textContent and would erase it. Opt in per button with
      // data-mh-price-label. `variant.price` is already formatted by mh-price, so no money
      // formatting happens here.
      var label = button.dataset.mhLabel || 'Add to Cart';
      if (button.hasAttribute('data-mh-price-label')) {
        label = label + ' · ' + variant.price;
      }

      button.textContent = variant.available ? label : 'Habis stok';
    });

    updateBuyNow(formId, variant.id);
  }

  function markUnavailable(formId, variants) {
    // A pill for a sold-out variant is dimmed but stays selectable — SPEC-PHASE0 §7.5 shows
    // what is out of stock rather than hiding it.
    forEach(scoped(formId, 'input[data-mh-option]'), function (input) {
      var index = Number(input.dataset.mhIndex);
      var anyAvailable = variants.some(function (v) {
        return (v.options || [])[index] === input.value && v.available;
      });
      var label = input.closest('.mh-buy__choice');
      if (label) label.classList.toggle('mh-buy__choice--unavailable', !anyAvailable);
    });
  }

  function init(form) {
    var formId = form.dataset.mhFormRoot;
    if (!formId) return;

    var variants = readVariants(formId);

    function sync() {
      apply(formId, matchVariant(variants, selectedOptions(formId)));
    }

    forEach(scoped(formId, 'input[data-mh-option]'), function (input) {
      input.addEventListener('change', sync);
    });

    forEach(scoped(formId, '[data-mh-step]'), function (button) {
      button.addEventListener('click', function () {
        var input = scoped(formId, '[data-mh-qty]')[0];
        if (!input) return;
        var next = currentQty(formId) + Number(button.dataset.mhStep);
        if (next < 1) next = 1;
        if (next > MAX_QTY) next = MAX_QTY;
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    forEach(scoped(formId, '[data-mh-qty]'), function (input) {
      input.addEventListener('change', function () {
        // Typing "0", "-4" or nothing at all has to resolve to a quantity that can be bought.
        input.value = currentQty(formId);
        var chosen = matchVariant(variants, selectedOptions(formId));
        if (chosen) updateBuyNow(formId, chosen.id);
      });
    });

    if (variants.length) {
      markUnavailable(formId, variants);
      sync();
    }
  }

  function start() {
    forEach(document.querySelectorAll('form[data-mh-form-root]'), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
